import type { NotificationDetail, PersistenceResult, SnapshotStore } from '@contember/bindx'
import { useBindxContext, usePersist } from '@contember/bindx-react'
import { Button, cn, LoaderIcon } from '@contember/bindx-ui'
import { type ComponentProps, type ReactNode, useCallback } from 'react'
import { useAdminToast } from './toast'
import { findValidationTarget, revealFirstInvalidField } from './validation-focus'

/**
 * Czech names for the entities a save can stumble over, for the case where the
 * form registered no target of its own. Coarse on purpose — it only has to be
 * better than the entity name bindx would otherwise print.
 */
const entityLabels: Record<string, string> = {
	Article: 'Článek',
	Block: 'Blok na stránce',
	BlockItem: 'Položka v bloku',
	ContactForm: 'Formulář',
	ContactFormField: 'Pole formuláře',
	ContentReference: 'Blok v textu',
	Course: 'Kurz',
	CourseTerm: 'Termín kurzu',
	Image: 'Obrázek',
	Link: 'Odkaz',
	NavigationItem: 'Položka navigace',
	Page: 'Stránka',
	GdPage: 'Stránka webu GD',
	GdChrome: 'Hlavička a patička GD',
	GdChromeLink: 'Odkaz v hlavičce nebo patičce GD',
	Person: 'Osoba',
	Podcast: 'Podcast',
	Tag: 'Štítek',
	Url: 'Adresa stránky',
}

/**
 * The handful of Contember's own validation messages an editor can meet. They
 * arrive in English from the API — the same strings `dict-cs.ts` translates for
 * inline form errors, repeated here because the toast shows the raw server text.
 */
const serverMessages: Record<string, string> = {
	'Field is required': 'Pole je povinné',
	'Value is not unique': 'Tahle hodnota už je obsazená',
}

const translateServerMessage = (message: string): string => serverMessages[message] ?? message

/** `[{ field: 'url' }, { field: 'url' }]` → `url.url`, for naming the rejected field. */
function formatFieldPath(path: readonly ({ field: string } | { index: number; alias: string | null })[]): string | undefined {
	const segments = path.map(segment => 'field' in segment ? segment.field : `[${segment.index + 1}]`)
	return segments.length > 0 ? segments.join('.') : undefined
}

/** Just enough of the bindx store to ask why a save was refused. */
type ValidationLookup = Pick<SnapshotStore, 'hasClientErrors'>

/**
 * A failure that never left the browser: `BatchPersister` returns before it builds a mutation, so
 * there is an `error` but no `mutationResult`. Deliberately narrow — a network failure is a server
 * failure and must keep its own message.
 */
function refusedBeforeTheRequest(entity: PersistenceResult['results'][number]): boolean {
	const error = entity.error
	if (!error || error.mutationResult) return false
	return /client validation/i.test(error.message ?? '')
}

export interface PersistFailure {
	/** One sentence, ready for a toast or an inline message. */
	message: string
	/** Server specifics, worth folding away under „Zobrazit podrobnosti“. */
	details: NotificationDetail[]
	/**
	 * Set only when the browser stopped the save: puts the offending field back
	 * on screen. A server rejection has nothing to reveal — the fields it
	 * complains about are already visible.
	 */
	reveal: (() => void) | null
}

/**
 * Turns a failed persist into something an editor can act on.
 *
 * Two very different failures arrive through the same result object:
 *
 * - **The browser refused.** A `required` input that was left empty puts a
 *   client-side error on its entity, and `BatchPersister` stops before the
 *   request — reporting `Entity Block:__temp_b78a… has client validation
 *   errors`. That id is the whole message bindx has; the entity behind it is a
 *   block whose form may not even be on screen. So: ask the registry what the
 *   thing is called, and offer to scroll back to it.
 * - **The server refused.** Then there are real field-level messages to show,
 *   and the fields are already where the editor left them.
 *
 * `hasClientErrors` first, because it is the same check `BatchPersister` makes and it survives a
 * reworded bindx error. It is not enough on its own, though: the store is read again *after* the
 * persist settles, and by then the form that registered the error may have unmounted (a block
 * whose panel the editor closed meanwhile), which clears it. The refusal itself is the second
 * witness — see {@link refusedBeforeTheRequest}.
 */
export function describePersistFailure(result: PersistenceResult, store: ValidationLookup): PersistFailure {
	for (const entity of result.results) {
		if (entity.success) continue
		if (!store.hasClientErrors(entity.entityType, entity.entityId) && !refusedBeforeTheRequest(entity)) continue

		const target = findValidationTarget(entity.entityType, entity.entityId)
		const subject = target?.label ?? entityLabels[entity.entityType]
		return {
			message: subject
				? `${subject} má nevyplněné povinné pole. Doplň ho a ulož znovu.`
				: 'Nějaké povinné pole zůstalo prázdné. Doplň zvýrazněné pole a ulož znovu.',
			details: [],
			reveal: target ? () => target.reveal() : () => void revealFirstInvalidField(),
		}
	}

	const details: NotificationDetail[] = []
	for (const entity of result.results) {
		if (entity.success || !entity.error) continue

		const subject = entityLabels[entity.entityType] ?? entity.entityType
		const mutation = entity.error.mutationResult

		// One line per rejected field where the server said which field it was.
		// Its own `message` repeats them all in one English blob, so it is the
		// fallback rather than a header.
		const validationErrors = mutation?.validation.errors ?? []
		for (const error of validationErrors) {
			details.push({
				entityType: subject,
				fieldName: formatFieldPath(error.path),
				message: translateServerMessage(error.message.text),
			})
		}
		for (const error of mutation?.errors ?? []) {
			details.push({ entityType: subject, message: translateServerMessage(error.message) })
		}
		if (validationErrors.length === 0 && (mutation?.errors.length ?? 0) === 0) {
			details.push({ entityType: subject, message: translateServerMessage(entity.error.message) })
		}
	}

	return { message: 'Uložení se nepovedlo.', details, reveal: null }
}

/**
 * `usePersistWithFeedback` from bindx, with the toasts written for editors.
 *
 * Same shape as the upstream hook, so it is a drop-in swap of the import. What
 * changes is the failure path: upstream hands the raw
 * `Entity Block:__temp_… has client validation errors` to the toast, because a
 * client-blocked save still arrives with an `error` object and reads as a
 * server failure to it.
 */
export function usePersistWithFeedback(): ReturnType<typeof usePersist> & {
	persistAllWithFeedback(options?: Parameters<ReturnType<typeof usePersist>['persistAll']>[0]): Promise<PersistenceResult>
} {
	const persistApi = usePersist()
	const { store } = useBindxContext()
	const toast = useAdminToast()

	const persistAllWithFeedback = useCallback(
		async (options?: Parameters<typeof persistApi.persistAll>[0]): Promise<PersistenceResult> => {
			const result = await persistApi.persistAll(options)
			if (result.success) {
				toast.success('Uloženo.')
				return result
			}

			const failure = describePersistFailure(result, store)
			if (failure.reveal) {
				// A warning, not an error: nothing broke, something is missing. Shorter than the
				// 15s default — the toast sits in the same corner as the block drawer's buttons,
				// and it has done its job the moment the editor is back at the field.
				toast.warning(failure.message, { dismissAfter: 8_000 })
				failure.reveal()
			} else {
				toast.error(failure.message, { details: failure.details.length > 0 ? failure.details : undefined })
			}

			return result
		},
		[persistApi, store, toast],
	)

	return { ...persistApi, persistAllWithFeedback }
}

export interface PersistButtonProps extends ComponentProps<typeof Button> {
	readonly label?: ReactNode
	readonly onSuccess?: () => void
	readonly onError?: () => void
}

/** bindx's `PersistButton`, wired to {@link usePersistWithFeedback} above. */
export function PersistButton({ label, className, onSuccess, onError, disabled, ...buttonProps }: PersistButtonProps): ReactNode {
	const { persistAllWithFeedback, isPersisting, isDirty } = usePersistWithFeedback()

	const handleClick = useCallback(async (): Promise<void> => {
		const result = await persistAllWithFeedback()
		if (result.success) onSuccess?.()
		else onError?.()
	}, [persistAllWithFeedback, onSuccess, onError])

	return (
		<Button
			className={cn('relative', className)}
			disabled={disabled || isPersisting || !isDirty}
			onClick={() => void handleClick()}
			{...buttonProps}
		>
			{isPersisting ? <LoaderIcon className="absolute" size="sm" /> : null}
			<span className={isPersisting ? 'invisible' : undefined}>{label ?? 'Uložit'}</span>
		</Button>
	)
}
